import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import { type Registry as Services, service } from '@ember/service';
import { runTask } from 'ember-lifeline';
import { Changeset } from 'ember-changeset';
import { TrackedSet } from 'tracked-built-ins';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import BaseController from 'uplisting-frontend/pods/base/controller';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import FormStepTracker from 'uplisting-frontend/utils/form-step-tracker';
import {
  checkBdcAddress,
  checkBdcCurrency,
  isValidBaseRate,
  isValidBathrooms,
  isValidBdcPhotos,
  isValidCapacity,
  isValidPropertyType,
} from 'uplisting-frontend/utils/property';
import type {
  AccessLevel,
  IVrboConnectionData,
} from 'uplisting-frontend/models/channel-connection';
import type ChannelListingModel from 'uplisting-frontend/models/channel-listing';
import type ChannelLinkModel from 'uplisting-frontend/models/channel-link';
import type { IChannelAccountData } from 'uplisting-frontend/models/channel-account';
import {
  type IPublishError,
  extractPublishErrors,
} from 'uplisting-frontend/utils/booking-dot-com-publish-errors';
import {
  getAirbnbReadLink,
  getAirbnbFullLink,
} from 'uplisting-frontend/utils/airbnb';
import {
  type IChannelStatusReading,
  type ILinkedProperty,
  type IListingActions,
  connectionStatus,
  linkedProperty,
  listingActions,
  listingStatus,
} from 'uplisting-frontend/utils/channel-status';
import config from 'uplisting-frontend/config/environment';
import {
  BDC_CONNECT_WIDGET_URL,
  BDC_CONTRACT_CHECK_ERRORS,
  BDC_LEGAL_ENTITY_ID_ERRORS,
} from 'uplisting-frontend/utils/bdc-constants';
import {
  type IVrboMappingErrors,
  type IVrboMappingPayload,
  type VrboMarket,
  compareByOriginalId,
  isVrboMarket,
  parseVrboMappingErrors,
} from 'uplisting-frontend/utils/vrbo';
import type {
  VrboConnectField,
  VrboModalMode,
} from 'uplisting-frontend/components/ui/modal/connect-channel';

const MAX_REFRESH_FAILURES = 5;
const VRBO_LISTINGS_RELOAD_DELAY = 30_000;

export enum ChannelOptions {
  airbnb = 'airbnb',
  bookingDotCom = 'booking_dot_com',
  vrbo = 'vrbo',
  // google = 'google',
  // direct = 'direct',
}

interface IChannelListingWithLink {
  listing: ChannelListingModel;
  link?: ChannelLinkModel;
}

interface IBdcPublishRequirement {
  valid: boolean;
  text: string;
  route: string;
  params?: Record<string, string>;
}

export default class ConnectListingsMappingController extends BaseController {
  @service analytics!: Services['analytics'];
  @service sentry!: Services['sentry'];
  @service features!: Services['features'];

  @service('repositories/photo')
  photoRepository!: Services['repositories/photo'];

  @service('repositories/channel-link')
  channelLinkRepository!: Services['repositories/channel-link'];

  @cached
  get connectFormTracker(): FormStepTracker {
    return new FormStepTracker(this.analytics, { formName: 'ota_connect' });
  }

  @cached
  get importFormTracker(): FormStepTracker {
    return new FormStepTracker(this.analytics, { formName: 'listing_import' });
  }

  queryParams = ['expand', 'channel'];

  channelOptions = ChannelOptions;
  bdcConnectWidgetUrl = BDC_CONNECT_WIDGET_URL;

  @tracked expand?: string;
  @tracked legalEntityId = '';
  @tracked legalEntityIdError?: string;
  @tracked isConnectingBookingDotCom = false;
  @tracked vrboCompanyName = '';
  @tracked vrboEmailAddress = '';
  @tracked vrboMarket?: VrboMarket;
  @tracked vrboConnectError?: string;
  @tracked isConnectingVrbo = false;
  @tracked vrboModalMode: VrboModalMode = 'connect';
  @tracked vrboConnectionToChange?: ModelRegistry['channel-connection'];
  @tracked vrboScrollTargetId?: string;
  @tracked vrboMappingErrors: Record<string, IVrboMappingErrors> = {};
  @tracked vrboMappingSubmittingId?: string;
  @tracked channel = ChannelOptions.airbnb;
  @tracked isLoading = false;
  @tracked isRefreshing = false;
  @tracked changesetToRefresh?: GenericChangeset<
    ModelRegistry['channel-connection']
  >;
  @tracked changesetToDeactivate?: GenericChangeset<
    ModelRegistry['channel-connection']
  >;
  @tracked changesetToUpgrade?: GenericChangeset<
    ModelRegistry['channel-connection']
  >;

  @tracked properties!: ModelRegistry['property'][];
  @tracked connections!: ModelRegistry['channel-connection'][];
  @tracked linksToCreate: ChannelLinkModel[] = [];

  @tracked showConnectModal = false;
  @tracked showListingMappingModal = false;
  @tracked showDeactivateModal = false;
  @tracked showUpgradeModal = false;
  @tracked showBookingDotComAdditionalListingsModal = false;
  @tracked showBdcPublishRequirementsModal = false;
  @tracked showVerificationModal = false;
  @tracked selectedBdcPublishPropertyId?: string;
  @tracked selectedBdcPublishConnection?: ModelRegistry['channel-connection'];
  @tracked isBdcPublishPropertyLoading = false;
  @tracked isPublishingBdcListing = false;
  @tracked showCreateBdcAccountModal = false;
  @tracked isCreatingBdcAccount = false;
  @tracked bdcPublishErrors: IPublishError[] = [];
  @tracked bdcAccountErrors: IPublishError[] = [];
  @tracked showDiscardDraftModal = false;
  @tracked listingToDiscard?: ChannelListingModel;
  @tracked listingToUnlink?: ChannelListingModel;

  #unlinking = new TrackedSet<ChannelListingModel>();
  #refreshing = new Set<string>();
  #refreshFailures = new Map<string, number>();
  #refreshGeneration = 0;

  @tracked accessLevel: AccessLevel = 'read_only';
  @tracked isInfoDropdownExpanded = false;

  @cached
  get channelReadable(): string {
    return this.intl.t(
      `connect_listings_mapping.channel_name.${this.channel}`,
      {
        channel: this.channel,
      },
    );
  }

  @cached
  get connectionChangesets(): GenericChangeset<
    ModelRegistry['channel-connection']
  >[] {
    return this.connections.map(
      (connection) =>
        Changeset(connection) as GenericChangeset<
          ModelRegistry['channel-connection']
        >,
    );
  }

  @cached
  get otaConnectionChangesets(): GenericChangeset<
    ModelRegistry['channel-connection']
  >[] {
    return this.connectionChangesets.filter(
      (changeset) => changeset.type !== ChannelOptions.vrbo,
    );
  }

  @cached
  get vrboConnectionChangesets(): GenericChangeset<
    ModelRegistry['channel-connection']
  >[] {
    return this.connectionChangesets
      .filter((changeset) => changeset.type === ChannelOptions.vrbo)
      .sort((a, b) => compareByOriginalId(a.data, b.data));
  }

  @cached
  get vrboAdvertiserToDeactivate(): string {
    const connection = this.changesetToDeactivate?._content as
      | ModelRegistry['channel-connection']
      | undefined;

    return connection?.vrboDisplayName ?? '';
  }

  @cached
  get channelListingsWithLinks(): IChannelListingWithLink[] {
    const { connectionChangesets } = this;

    return connectionChangesets.flatMap((item) => {
      // TODO remove conditional map
      return item.channelListings?.map((listing: ChannelListingModel) => ({
        listing,
        link: item.channelLinks?.find(
          (link: ChannelLinkModel) => link.externalId === listing.externalId,
        ),
      }));
    }) as IChannelListingWithLink[];
  }

  @cached
  get connectableBdcProperties(): ModelRegistry['property'][] {
    return this.properties.filter(
      (property) => !this.isPropertyConnected(property),
    );
  }

  @cached
  get selectedBdcPublishProperty(): ModelRegistry['property'] | undefined {
    return this.properties.find(
      (property) => property.id === this.selectedBdcPublishPropertyId,
    );
  }

  @cached
  get requiresIdentityVerification(): boolean {
    return (
      this.channel === ChannelOptions.bookingDotCom &&
      !this.session.currentUser?.verified
    );
  }

  @cached
  get bdcPublishRequirements(): IBdcPublishRequirement[] {
    const property = this.selectedBdcPublishProperty;

    if (!property) {
      return [];
    }

    return [
      {
        valid: isValidPropertyType(property),
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.property_type',
        route: 'calendar.properties.details.basics',
      },
      {
        valid: isValidCapacity(property),
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.max_guests',
        route: 'calendar.properties.details.basics',
      },
      {
        valid: isValidBathrooms(property),
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.bathrooms',
        route: 'calendar.properties.details.basics',
      },
      {
        valid: true,
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.suitabilities',
        route: 'calendar.properties.details.basics',
      },
      {
        valid: isValidBdcPhotos(property),
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.photos',
        route: 'calendar.properties.details.photos',
      },
      this.#addressRequirement(property),
      this.#currencyRequirement(property),
      {
        valid: true,
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.amenities',
        route: 'calendar.properties.details.amenities',
      },
      {
        valid: isValidBaseRate(property),
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.base_rates',
        route: 'calendar.properties.pricing.base',
      },
      {
        valid: true,
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.charges_and_fees',
        route: 'calendar.properties.pricing.fees',
      },
      {
        valid: true,
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.discounts',
        route: 'calendar.properties.pricing.discounts',
      },
      {
        valid: true,
        text: 'connect_listings_mapping.publish_requirements_modal.requirements.availability_settings',
        route: 'calendar.properties.restrictions',
      },
    ];
  }

  @cached
  get isBdcPublishable(): boolean {
    if (!this.selectedBdcPublishPropertyId) {
      return false;
    }

    return this.bdcPublishRequirements.every(
      (requirement) => requirement.valid,
    );
  }

  @action
  handleLegalEntityIdChange(event: Event): void {
    const { value } = event.target as HTMLInputElement;

    this.legalEntityId = value;
    this.legalEntityIdError = undefined;
  }

  @action
  handleExpandChangeset(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): void {
    const isCurrentlyCollapsed = changeset.isCollapsed;

    this.connectionChangesets
      .filter((item) => item !== changeset)
      .forEach((item) => {
        item.isCollapsed = true;
      });

    changeset.isCollapsed = !isCurrentlyCollapsed;
  }

  @action
  computeConnectionStatus(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): IChannelStatusReading {
    return connectionStatus(
      changeset._content as ModelRegistry['channel-connection'],
    );
  }

  @action
  computeCanReconnectAirbnbConnection(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): boolean {
    return (
      changeset.type === ChannelOptions.airbnb &&
      changeset.status === 'inactive'
    );
  }

  @action
  computeReconnectLinkFor(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): string {
    const redirectUri = config.airbnbOfficial.connectListingsCallbackUri;

    return changeset.accessLevel === 'read_only'
      ? getAirbnbReadLink(redirectUri)
      : getAirbnbFullLink(redirectUri, 'full');
  }

  @action
  handleShowConnectModal(): void {
    // TODO: finalize our flows in which cases modal is shown
    // if (this.channel === ChannelOptions.direct) {
    //   window.alert('implement me');

    //   return;
    // }

    if (this.requiresIdentityVerification) {
      this.showVerificationModal = true;

      return;
    }

    this.connectFormTracker.markStarted({ channel: this.channel });

    this.#resetLegalEntityId();
    this.#resetVrboForm();
    this.showConnectModal = true;
  }

  @action
  handleShowEditVrboModal(
    connection: ModelRegistry['channel-connection'],
  ): void {
    this.#openVrboModalFor(connection, 'edit');
  }

  @action
  handleShowRetryVrboModal(
    connection: ModelRegistry['channel-connection'],
  ): void {
    this.#openVrboModalFor(connection, 'retry');
  }

  @action
  handleChangeVrboMapping(): void {
    this.importFormTracker.markStarted({ channel: ChannelOptions.vrbo });
  }

  @action
  @waitFor
  async handleSubmitVrboMapping(
    connection: ModelRegistry['channel-connection'],
    payload: IVrboMappingPayload,
  ): Promise<void> {
    if (this.vrboMappingSubmittingId) {
      return;
    }

    this.vrboMappingSubmittingId = connection.id;
    this.#setVrboMappingErrors(connection, undefined);

    const savedLinks = [...connection.channelLinks];
    const links = payload.links.map(({ externalId, propertyId }) =>
      this.store.createRecord('channel-link', {
        externalId,
        propertyId,
        channelConnection: connection,
      }),
    );

    connection.channelLinks = links;
    connection.data = {
      ...connection.data,
      archive_external_ids: payload.archiveExternalIds,
    };

    try {
      await connection.save();

      this.#unloadNewLinks(links);

      this.importFormTracker.markSubmitted({ channel: ChannelOptions.vrbo });
      this.analytics.trackEvent(AnalyticsEvents.listingImported, {
        channel: ChannelOptions.vrbo,
        count: payload.links.length,
      });

      this.refresh(connection);
    } catch (error) {
      this.#unloadNewLinks(links);
      connection.channelLinks = savedLinks.filter(
        (link) => !link.isDestroying && !link.isDestroyed,
      );
      connection.rollbackAttributes();

      await this.#handleVrboMappingError(connection, error);
    } finally {
      this.vrboMappingSubmittingId = undefined;
    }
  }

  @action
  handleCloseConnectModal(): void {
    if (this.vrboModalMode === 'connect') {
      this.connectFormTracker.teardown({ channel: this.channel });
    }

    this.#resetLegalEntityId();
    this.#resetVrboForm();
    this.showConnectModal = false;
  }

  @action
  handleVrboFieldChange(field: VrboConnectField, value: string): void {
    if (field === 'companyName') {
      this.vrboCompanyName = value;
    } else if (field === 'emailAddress') {
      this.vrboEmailAddress = value;
    } else {
      this.vrboMarket = isVrboMarket(value) ? value : undefined;
    }

    this.vrboConnectError = undefined;
  }

  @action
  handleToggleInfoDropdown(): void {
    this.isInfoDropdownExpanded = !this.isInfoDropdownExpanded;
  }

  @action
  handleSelectAll(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): void {
    const { channelLinks, channelListings } = changeset;

    const links = (channelListings as ChannelListingModel[]).map(
      (listing: ChannelListingModel) => {
        const link = (channelLinks as ChannelLinkModel[]).find(
          (link: ChannelLinkModel) => link.externalId === listing.externalId,
        );

        if (link) {
          return link;
        }

        return this.store.createRecord('channel-link', {
          externalId: listing.externalId,
          propertyId: null,
          channelConnection:
            changeset._content as ModelRegistry['channel-connection'],
        });
      },
    );

    changeset.channelLinks = links as ModelRegistry['channel-link'][];
  }

  @action
  @waitFor
  async handleCreateConnection(): Promise<void> {
    if (this.channel === ChannelOptions.airbnb) {
      this.connectFormTracker.markSubmitted({ channel: this.channel });
      this.#onAirbnbSubmit();
    } else if (this.channel === ChannelOptions.bookingDotCom) {
      await this.#onBookingDotComSubmit();
    } else if (this.channel === ChannelOptions.vrbo) {
      await this.#onVrboSubmit();
    }
  }

  @action
  handleSelectAccessLevel(accessLevel: AccessLevel): void {
    this.accessLevel = accessLevel;
    this.isInfoDropdownExpanded = false;
  }

  @action
  handleShowUpgradeModal(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): void {
    this.changesetToUpgrade = changeset;
    this.showUpgradeModal = true;
  }

  @action
  @waitFor
  async handleSubmitImportListings(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): Promise<void> {
    try {
      await changeset.save();

      this.analytics.trackEvent(AnalyticsEvents.listingImported, {
        channel: changeset.type,
        count: changeset.channelLinks?.length ?? 0,
      });
      this.importFormTracker.markSubmitted({ channel: changeset.type });

      this.refresh(changeset._content as ModelRegistry['channel-connection']);
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    }
  }

  @action
  handleToggleImportListing(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
    listing: ChannelListingModel,
  ): void {
    this.importFormTracker.markStarted({ channel: changeset.type });

    const channelLinks = (changeset.channelLinks ??
      []) as ModelRegistry['channel-link'][];

    if (this.isSelectedForImport(changeset, listing)) {
      changeset.channelLinks = [
        ...channelLinks.filter(
          (link: ChannelLinkModel) => link.externalId !== listing.externalId,
        ),
      ] as ModelRegistry['channel-link'][];

      return;
    }

    const newLink = this.store.createRecord('channel-link', {
      externalId: listing.externalId,
      propertyId: null,
      channelConnection:
        changeset._content as ModelRegistry['channel-connection'],
    });

    changeset.channelLinks = [
      ...channelLinks,
      newLink,
    ] as ModelRegistry['channel-link'][];
  }

  @action
  isSelectedForImport(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
    listing: ChannelListingModel,
  ): boolean {
    return !!changeset.channelLinks?.find(
      (x: ChannelLinkModel) => x.externalId === listing.externalId,
    );
  }

  @action
  handleSelectProperty(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
    property: ModelRegistry['property'],
    listing: ChannelListingModel,
  ): void {
    this.importFormTracker.markStarted({ channel: changeset.type });

    const channelLinks = (changeset.channelLinks ??
      []) as ModelRegistry['channel-link'][];

    const newLink = this.store.createRecord('channel-link', {
      externalId: listing.externalId,
      propertyId: property.id,
      channelConnection:
        changeset._content as ModelRegistry['channel-connection'],
    });

    changeset.channelLinks = [
      ...channelLinks,
      newLink,
    ] as ModelRegistry['channel-link'][];
  }

  @action
  @waitFor
  async handleRefreshChangeset(
    changeset?: GenericChangeset<ModelRegistry['channel-connection']>,
  ): Promise<void> {
    if (!changeset) {
      return;
    }

    const model = changeset._content as ModelRegistry['channel-connection'];

    Object.assign(this, {
      isRefreshing: true,
      changesetToRefresh: changeset,
    });

    try {
      await model.reload({ adapterOptions: { refresh_listings: true } });

      this.showBookingDotComAdditionalListingsModal = false;

      if (model.type === ChannelOptions.vrbo) {
        this.notifications.info(
          'connect_listings_mapping.vrbo.fetching_listings',
        );
        this.#reloadVrboListingsLater(model);
      }
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    } finally {
      Object.assign(this, {
        isRefreshing: false,
        changesetToRefresh: undefined,
      });
    }
  }

  @action
  isPropertyConnected(property: ModelRegistry['property']): boolean {
    return this.channelListingsWithLinks.some(
      (item) => item.link?.propertyId === property.id,
    );
  }

  @action
  handleShowCreateBdcAccountModal(): void {
    if (this.requiresIdentityVerification) {
      this.showVerificationModal = true;

      return;
    }

    this.bdcAccountErrors = [];
    this.showCreateBdcAccountModal = true;
  }

  @action
  handleCloseVerificationModal(): void {
    this.showVerificationModal = false;
  }

  @action
  handleShowBdcPublishRequirementsModal(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): void {
    this.selectedBdcPublishConnection =
      changeset._content as ModelRegistry['channel-connection'];
    this.bdcPublishErrors = [];
    this.showBdcPublishRequirementsModal = true;
  }

  @action
  handleCloseBdcPublishRequirementsModal(): void {
    this.showBdcPublishRequirementsModal = false;
    this.selectedBdcPublishPropertyId = undefined;
    this.selectedBdcPublishConnection = undefined;
    this.isBdcPublishPropertyLoading = false;
    this.bdcPublishErrors = [];
  }

  @cached
  get isSubmitDisabled(): boolean {
    return !this.isBdcPublishable || this.isPublishingBdcListing;
  }

  @action
  @waitFor
  async handlePublishBdcListing(): Promise<void> {
    if (this.isSubmitDisabled) {
      return;
    }

    const { selectedBdcPublishProperty, selectedBdcPublishConnection } = this;

    if (!selectedBdcPublishProperty || !selectedBdcPublishConnection) {
      return;
    }

    this.isPublishingBdcListing = true;
    this.bdcPublishErrors = [];

    const record = this.store.createRecord('channel-listing', {
      property: selectedBdcPublishProperty,
      channelConnection: selectedBdcPublishConnection,
    });

    try {
      await record.save();
    } catch (error) {
      record.unloadRecord();
      this.bdcPublishErrors = extractPublishErrors(error);

      if (this.bdcPublishErrors.length === 0) {
        this.notifications.error();
      }

      this.isPublishingBdcListing = false;
      return;
    }

    this.isPublishingBdcListing = false;

    this.notifications.info('connect_listings_mapping.publish_listing_success');

    try {
      await this.router.transitionTo('connect.listings.publish', {
        queryParams: {
          property_id: this.selectedBdcPublishPropertyId,
          step: 'property_settings',
          channel: ChannelOptions.bookingDotCom,
        },
      });
    } catch (error) {
      // here `TransitionAborted` is being thrown for some reason
      if ((error as Error).message !== 'TransitionAborted') {
        throw error;
      }
    }
  }

  @action
  @waitFor
  async handleSelectBdcPublishProperty(
    property: ModelRegistry['property'],
  ): Promise<void> {
    this.isBdcPublishPropertyLoading = true;
    this.selectedBdcPublishPropertyId = undefined;
    this.bdcPublishErrors = [];

    try {
      await Promise.all([
        this.store.findRecord('property', property.id, { reload: true }),
        this.photoRepository.query({ property_id: property.id }),
      ]);

      this.selectedBdcPublishPropertyId = property.id;
    } catch {
      this.notifications.error();
    } finally {
      this.isBdcPublishPropertyLoading = false;
    }
  }

  @action
  handleCloseCreateBdcAccountModal(): void {
    this.showCreateBdcAccountModal = false;
    this.bdcAccountErrors = [];
  }

  @action
  @waitFor
  async handleCreateBdcAccount(data: IChannelAccountData): Promise<boolean> {
    this.isCreatingBdcAccount = true;
    this.bdcAccountErrors = [];
    const record = this.store.createRecord('channel-account', {
      type: 'booking_dot_com',
      data,
    });

    try {
      await record.save();

      this.notifications.info('connect_listings_mapping.account_created');

      // Force a fresh fetch to pick up the newly created BDC connection.
      const allConnections = await this.store.findAll('channel-connection', {
        reload: true,
      });

      this.connections = allConnections.filter(
        (connection) => connection.type === this.channel,
      );

      return true;
    } catch (error) {
      const status = (error as { errors?: { status?: string }[] })?.errors?.[0]
        ?.status;

      if (record.isNew) {
        record.unloadRecord();
      }

      if (status === '409') {
        this.notifications.error(
          'connect_listings_mapping.account_already_exists',
        );
      } else {
        this.bdcAccountErrors = extractPublishErrors(error);

        if (this.bdcAccountErrors.length === 0) {
          this.notifications.error();
        }
      }

      return false;
    } finally {
      this.isCreatingBdcAccount = false;
    }
  }

  #onAirbnbSubmit(): void {
    const redirectUri = config.airbnbOfficial.connectListingsCallbackUri;
    const link =
      this.accessLevel === 'read_only'
        ? getAirbnbReadLink(redirectUri)
        : getAirbnbFullLink(redirectUri, 'full');

    window.open(`${link}`, '_self');
  }

  @action
  @waitFor
  async handleUpgrade(): Promise<void> {
    if (!this.changesetToUpgrade?.isReadOnly) {
      return;
    }

    const redirectUri = config.airbnbOfficial.connectListingsCallbackUri;
    const link = getAirbnbFullLink(
      `${redirectUri}`,
      this.changesetToUpgrade?.id,
    );

    window.open(`${link}`, '_self');
  }

  @action
  computeListingStatus(listing: ChannelListingModel): IChannelStatusReading {
    return listingStatus(listing, listing.channelConnection);
  }

  @action
  computeLinkedProperty(listing: ChannelListingModel): ILinkedProperty {
    return linkedProperty(listing, this.properties);
  }

  @action
  computeListingActions(listing: ChannelListingModel): IListingActions {
    if (this.#unlinking.has(listing)) {
      return { isUnlinking: true };
    }

    return listingActions(listing, listing.channelConnection);
  }

  @action
  handleShowBookingDotComAdditionalListingsModal(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): void {
    this.showBookingDotComAdditionalListingsModal = true;
    this.changesetToRefresh = changeset;
  }

  @action
  handleShowDeactivateModal(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): void {
    this.showDeactivateModal = true;
    this.changesetToDeactivate = changeset;
  }

  @action
  handleCloseDeactivateModal(): void {
    this.showDeactivateModal = false;
  }

  @action
  @waitFor
  async handleDeactivate(): Promise<void> {
    const changeset = this.changesetToDeactivate;

    try {
      await changeset?.data?.destroyRecord();
      this.connections = this.connections.filter(
        (connection) => connection.id !== changeset?.id,
      );

      this.notifications.info('connect_listings_mapping.deactivated');
    } catch (error) {
      this.#restoreAfterFailedDeactivate(changeset?.data);
      this.sentry.captureException(error);
      this.notifications.error();
    } finally {
      this.showDeactivateModal = false;
      this.changesetToDeactivate = undefined;
    }
  }

  @action
  handleShowDiscardDraftModal(listing: ChannelListingModel): void {
    this.listingToDiscard = listing;
    this.showDiscardDraftModal = true;
  }

  @action
  handleCloseDiscardDraftModal(): void {
    this.showDiscardDraftModal = false;
    this.listingToDiscard = undefined;
  }

  @action
  @waitFor
  async handleDiscardDraft(): Promise<void> {
    const listing = this.listingToDiscard;
    const publication = listing?.bookingDotComHotelPublication;

    this.handleCloseDiscardDraftModal();

    if (!listing || !publication) {
      return;
    }

    try {
      await publication.destroyRecord();

      listing.channelConnection.channelLinks
        .filter((link) => link.externalId === listing.externalId)
        .forEach((link) => link.unloadRecord());

      publication.unloadRecord();
      listing.unloadRecord();

      this.notifications.info('connect_listings_mapping.draft_discarded');
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    }
  }

  @action
  handleShowUnlinkModal(listing: ChannelListingModel): void {
    this.listingToUnlink = listing;
  }

  @action
  handleCloseUnlinkModal(): void {
    this.listingToUnlink = undefined;
  }

  @action
  @waitFor
  async handleUnlinkListing(): Promise<void> {
    const listing = this.listingToUnlink;
    const link = listing && this.computeListingActions(listing).link;

    this.handleCloseUnlinkModal();

    if (!listing || !link) {
      return;
    }

    this.#unlinking.add(listing);

    try {
      await this.channelLinkRepository.unlink(link);

      this.notifications.info('connect_listings_mapping.listing_unlinked', {
        name: listing.displayName,
      });
    } catch {
      this.notifications.error();
    } finally {
      this.#unlinking.delete(listing);
    }
  }

  @action
  @waitFor
  async handleCheckContract(
    changeset: GenericChangeset<ModelRegistry['channel-connection']>,
  ): Promise<void> {
    const connection =
      changeset._content as ModelRegistry['channel-connection'];

    Object.assign(this, { isRefreshing: true, changesetToRefresh: changeset });

    try {
      await connection.reload({ adapterOptions: { check_contract: true } });

      this.notifications.info(
        connection.isPendingContract
          ? 'connect_listings_mapping.contract.not_signed'
          : 'connect_listings_mapping.contract.signed',
      );
    } catch (error) {
      const code = this.#errorCode(error);

      if (code && BDC_CONTRACT_CHECK_ERRORS.includes(code)) {
        this.notifications.error(
          `connect_listings_mapping.contract.errors.${code}`,
        );
      } else {
        this.sentry.captureException(error);
        this.notifications.error();
      }
    } finally {
      Object.assign(this, {
        isRefreshing: false,
        changesetToRefresh: undefined,
      });
    }
  }

  #addressRequirement(
    property: ModelRegistry['property'],
  ): IBdcPublishRequirement {
    const check = checkBdcAddress(property);
    const key =
      check.status === 'unsupported_country' ||
      check.status === 'unsupported_state'
        ? check.status
        : 'set';

    return {
      valid: check.status === 'valid',
      text: `connect_listings_mapping.publish_requirements_modal.requirements.address.${key}`,
      route: 'calendar.properties.details.address',
      params: {
        country: this.#countryDisplayName(
          check.countryCode,
          property.address?.country,
        ),
      },
    };
  }

  #currencyRequirement(
    property: ModelRegistry['property'],
  ): IBdcPublishRequirement {
    const check = checkBdcCurrency(property);

    return {
      valid: check.status === 'valid',
      text: `connect_listings_mapping.publish_requirements_modal.requirements.currency.${check.status}`,
      route:
        check.status === 'unresolved_country'
          ? 'calendar.properties.details.address'
          : 'calendar.properties.pricing.base',
      params: {
        currency: property.currency ?? '',
        country: this.#countryDisplayName(
          check.countryCode,
          property.address?.country,
        ),
        accepted: check.acceptedCurrencies.join(', '),
      },
    };
  }

  #countryDisplayName(code?: string, fallback?: string): string {
    const key = `countries_list.${code}`;

    if (code && this.intl.exists(key)) {
      return this.intl.t(key);
    }

    return fallback ?? '';
  }

  async #onBookingDotComSubmit(): Promise<void> {
    const legalEntityId = this.legalEntityId.trim();

    if (!/^\d+$/.test(legalEntityId)) {
      this.legalEntityIdError = 'invalid_format';

      return;
    }

    const record = this.store.createRecord('channel-connection', {
      type: 'booking_dot_com',
      accessLevel: 'full',
      data: {
        legal_entity_id: legalEntityId,
      },
    });

    this.isConnectingBookingDotCom = true;

    try {
      await record.save();

      if (record.isOnboarding) {
        window.open(this.bdcConnectWidgetUrl, '_blank', 'noopener,noreferrer');
      }

      this.connectFormTracker.markSubmitted({ channel: this.channel });
      this.analytics.trackEvent(AnalyticsEvents.otaConnected, {
        channel: ChannelOptions.bookingDotCom,
      });

      this.connections = [...this.connections, record];
      record.isCollapsed = false;

      this.handleCloseConnectModal();
      this.refresh(record);
    } catch (error) {
      record.unloadRecord();

      const code = this.#errorCode(error);

      if (code && BDC_LEGAL_ENTITY_ID_ERRORS.includes(code)) {
        this.legalEntityIdError = code;
      } else {
        this.notifications.error();
      }
    } finally {
      this.isConnectingBookingDotCom = false;
    }
  }

  #resetLegalEntityId(): void {
    this.legalEntityId = '';
    this.legalEntityIdError = undefined;
  }

  async #onVrboSubmit(): Promise<void> {
    const connection = this.vrboConnectionToChange;

    if (connection && this.vrboModalMode === 'edit') {
      await this.#onVrboEdit(connection);
    } else if (connection && this.vrboModalMode === 'retry') {
      await this.#onVrboRetry(connection);
    } else {
      await this.#onVrboCreate();
    }
  }

  async #onVrboCreate(): Promise<void> {
    const record = this.store.createRecord('channel-connection', {
      type: ChannelOptions.vrbo,
      accessLevel: 'full',
      data: this.#vrboDetails(),
    });

    this.isConnectingVrbo = true;
    this.vrboConnectError = undefined;

    try {
      await record.save();

      this.connectFormTracker.markSubmitted({ channel: ChannelOptions.vrbo });
      this.analytics.trackEvent(AnalyticsEvents.otaConnected, {
        channel: ChannelOptions.vrbo,
      });

      this.connections = [...this.connections, record];
      this.#expandOnly(record);

      this.handleCloseConnectModal();
      this.refresh(record);
    } catch (error) {
      record.unloadRecord();

      await this.#handleVrboConnectError(error);
    } finally {
      this.isConnectingVrbo = false;
    }
  }

  async #onVrboEdit(
    record: ModelRegistry['channel-connection'],
  ): Promise<void> {
    this.isConnectingVrbo = true;
    this.vrboConnectError = undefined;

    try {
      record.data = { ...record.data, ...this.#vrboDetails() };

      await record.save();

      this.notifications.info('connect_listings_mapping.vrbo.edited');
      this.handleCloseConnectModal();
      this.refresh(record);
    } catch (error) {
      record.rollbackAttributes();

      await this.#handleVrboChangeError(record, error, 'edit');
    } finally {
      this.isConnectingVrbo = false;
    }
  }

  async #onVrboRetry(
    record: ModelRegistry['channel-connection'],
  ): Promise<void> {
    this.isConnectingVrbo = true;
    this.vrboConnectError = undefined;

    try {
      record.data = { ...record.data, ...this.#vrboDetails(), retry: true };

      await record.save();

      this.analytics.trackEvent(AnalyticsEvents.otaConnected, {
        channel: ChannelOptions.vrbo,
      });

      this.handleCloseConnectModal();
      this.#expandOnly(record);
      this.refresh(record);
    } catch (error) {
      record.rollbackAttributes();

      await this.#handleVrboChangeError(record, error, 'retry');
    } finally {
      this.isConnectingVrbo = false;
    }
  }

  async #handleVrboChangeError(
    record: ModelRegistry['channel-connection'],
    error: unknown,
    mode: Exclude<VrboModalMode, 'connect'>,
  ): Promise<void> {
    const status = this.#errorStatus(error);

    if (status === 409) {
      this.handleCloseConnectModal();
      this.notifications.error(
        mode === 'edit'
          ? 'connect_listings_mapping.vrbo.not_editable'
          : 'connect_listings_mapping.vrbo.not_retryable',
      );

      await this.#reloadVrboConnection(record);

      return;
    }

    if (status === 404) {
      this.handleCloseConnectModal();
      this.notifications.error();

      await this.#dropVrboConnection(record);

      return;
    }

    if (status !== 400 && status !== 502) {
      this.sentry.captureException(error);
      this.notifications.error();

      return;
    }

    const title = this.#vrboErrorTitle(error);

    if (!title) {
      this.sentry.captureException(error);
    }

    if (mode === 'retry' && status === 502) {
      this.handleCloseConnectModal();

      await this.#reloadVrboConnection(record);

      this.#expandOnly(record);
      this.vrboScrollTargetId = record.id;

      return;
    }

    this.vrboConnectError =
      title ?? this.intl.t(`ui_modal_connect_channel.vrbo.${mode}.error`);
  }

  async #handleVrboMappingError(
    connection: ModelRegistry['channel-connection'],
    error: unknown,
  ): Promise<void> {
    const status = this.#errorStatus(error);

    if (status === 409) {
      this.notifications.error(
        'connect_listings_mapping.vrbo.mapping_already_submitted',
      );

      await this.#reloadVrboConnection(connection);

      if (connection.isAwaitingChannel) {
        this.refresh(connection);
      }

      return;
    }

    if (status === 404) {
      this.notifications.error();

      await this.#dropVrboConnection(connection);

      return;
    }

    const title = status === 400 ? this.#vrboErrorTitle(error) : undefined;

    if (!title) {
      this.sentry.captureException(error);
      this.notifications.error();

      return;
    }

    this.#setVrboMappingErrors(connection, parseVrboMappingErrors(title));

    await this.#reloadVrboConnection(connection);
  }

  #setVrboMappingErrors(
    connection: ModelRegistry['channel-connection'],
    errors: IVrboMappingErrors | undefined,
  ): void {
    const others = Object.fromEntries(
      Object.entries(this.vrboMappingErrors).filter(
        ([id]) => id !== connection.id,
      ),
    );

    this.vrboMappingErrors = errors
      ? { ...others, [connection.id]: errors }
      : others;
  }

  async #dropVrboConnection(
    record: ModelRegistry['channel-connection'],
  ): Promise<void> {
    this.connections = this.connections.filter(
      (connection) => connection.id !== record.id,
    );
    record.unloadRecord();

    await this.#reloadConnections();
  }

  #unloadNewLinks(links: ChannelLinkModel[]): void {
    links
      .filter((link) => link.isNew && !link.isDestroying && !link.isDestroyed)
      .forEach((link) => link.unloadRecord());
  }

  async #reloadVrboConnection(
    record: ModelRegistry['channel-connection'],
  ): Promise<void> {
    try {
      await record.reload();
    } catch (error) {
      this.sentry.captureException(error);
    }
  }

  #vrboErrorTitle(error: unknown): string | undefined {
    const [apiError] =
      (error as { errors?: { code?: string; title?: string }[] })?.errors ?? [];

    return apiError?.code ? apiError.title : undefined;
  }

  #vrboDetails(): Pick<
    IVrboConnectionData,
    'company_name' | 'email_address' | 'market'
  > {
    return {
      company_name: this.vrboCompanyName.trim(),
      email_address: this.vrboEmailAddress.trim(),
      market: this.vrboMarket ?? null,
    };
  }

  #openVrboModalFor(
    connection: ModelRegistry['channel-connection'],
    mode: Exclude<VrboModalMode, 'connect'>,
  ): void {
    const {
      company_name: companyName,
      email_address: emailAddress,
      market,
    } = connection.vrboData;

    this.vrboModalMode = mode;
    this.vrboConnectionToChange = connection;
    this.vrboCompanyName = companyName ?? '';
    this.vrboEmailAddress = emailAddress ?? '';
    this.vrboMarket = isVrboMarket(market) ? market : undefined;
    this.vrboConnectError = undefined;
    this.showConnectModal = true;
  }

  async #handleVrboConnectError(error: unknown): Promise<void> {
    const status = this.#errorStatus(error);
    const title = this.#vrboErrorTitle(error);

    if (status === 400 || status === 502) {
      const message =
        title ?? this.intl.t('ui_modal_connect_channel.vrbo.errors.generic');

      if (!title) {
        this.sentry.captureException(error);
      }

      if (status === 502) {
        await this.#showFailedVrboConnection(message);
      } else {
        this.vrboConnectError = message;
      }

      return;
    }

    this.sentry.captureException(error);
    this.notifications.error();
  }

  async #showFailedVrboConnection(message: string): Promise<void> {
    const knownIds = new Set(this.connections.map(({ id }) => id));

    await this.#reloadConnections();

    const failed = this.connections.find(
      (connection) => !knownIds.has(connection.id) && connection.isFailed,
    );

    this.connectFormTracker.markSubmitted({ channel: ChannelOptions.vrbo });
    this.handleCloseConnectModal();

    if (failed) {
      this.#expandOnly(failed);
      this.vrboScrollTargetId = failed.id;
    } else {
      this.notifications.error(message, true);
    }
  }

  async #reloadConnections(): Promise<void> {
    try {
      const allConnections = await this.store.findAll('channel-connection', {
        reload: true,
      });

      this.connections = allConnections.filter(
        (connection) => connection.type === this.channel,
      );
    } catch (error) {
      this.sentry.captureException(error);
    }
  }

  #restoreAfterFailedDeactivate(
    record: ModelRegistry['channel-connection'] | undefined,
  ): void {
    if (!record || record.isDestroying || record.isDestroyed) {
      return;
    }

    record.rollbackAttributes();

    if (record.isAwaitingChannel) {
      this.refresh(record);
    }
  }

  #expandOnly(record: ModelRegistry['channel-connection']): void {
    this.connections.forEach((connection) => {
      connection.isCollapsed = connection !== record;
    });
  }

  #resetVrboForm(): void {
    this.vrboModalMode = 'connect';
    this.vrboConnectionToChange = undefined;
    this.vrboCompanyName = '';
    this.vrboEmailAddress = '';
    this.vrboMarket = undefined;
    this.vrboConnectError = undefined;
  }

  async refresh(
    model: ModelRegistry['channel-connection'],
    { immediate = false }: { immediate?: boolean } = {},
  ): Promise<void> {
    if (model.isDeleted || model.isDestroying || model.isDestroyed) {
      return;
    }

    if (!model.isAwaitingChannel) {
      this.router.transitionTo('connect.listings.mapping.index', {
        queryParams: { expand: model.id },
      });

      return;
    }

    if (this.#refreshing.has(model.id)) {
      return;
    }

    let delay = model.type === ChannelOptions.airbnb ? 10_000 : 60_000;

    if (config.IS_TEST) {
      delay = 100;
    }

    const generation = this.#refreshGeneration;

    this.#refreshing.add(model.id);

    runTask(
      this,
      async () => {
        if (generation !== this.#refreshGeneration) {
          return;
        }

        this.#refreshing.delete(model.id);

        if (model.isDeleted || model.isDestroying || model.isDestroyed) {
          return;
        }

        try {
          const reloadParams =
            model.channelListings?.length === 0
              ? { adapterOptions: { refresh_listings: true } }
              : undefined;

          await model.reload(reloadParams);

          this.#refreshFailures.delete(model.id);
        } catch (error) {
          if (!this.#shouldRetryRefresh(model, error)) {
            return;
          }
        }

        if (generation === this.#refreshGeneration) {
          this.refresh(model);
        }
      },
      immediate ? 0 : delay,
    );
  }

  stopRefreshing(): void {
    this.#refreshGeneration += 1;
    this.#refreshing.clear();
    this.#refreshFailures.clear();
  }

  #reloadVrboListingsLater(model: ModelRegistry['channel-connection']): void {
    const generation = this.#refreshGeneration;

    runTask(
      this,
      async () => {
        if (
          generation !== this.#refreshGeneration ||
          model.isDeleted ||
          model.isDestroying ||
          model.isDestroyed
        ) {
          return;
        }

        try {
          await model.reload();
        } catch (error) {
          this.sentry.captureException(error);
        }
      },
      config.IS_TEST ? 100 : VRBO_LISTINGS_RELOAD_DELAY,
    );
  }

  #shouldRetryRefresh(
    model: ModelRegistry['channel-connection'],
    error: unknown,
  ): boolean {
    const status = this.#errorStatus(error);

    if (status === 404) {
      this.connections = this.connections.filter(
        (connection) => connection.id !== model.id,
      );
      model.unloadRecord();

      return false;
    }

    if (status === 401 || status === 403) {
      return false;
    }

    const failures = (this.#refreshFailures.get(model.id) ?? 0) + 1;

    if (failures < MAX_REFRESH_FAILURES) {
      this.#refreshFailures.set(model.id, failures);

      return true;
    }

    this.#refreshFailures.delete(model.id);
    this.sentry.captureException(error);
    this.notifications.error(
      'connect_listings_mapping.onboarding.refresh_failed',
    );

    return false;
  }

  #errorStatus(error: unknown): number | undefined {
    const { errors, status } = (error ?? {}) as {
      errors?: { status?: string }[];
      status?: number;
    };

    return Number(errors?.[0]?.status ?? status) || undefined;
  }

  #errorCode(error: unknown): string | undefined {
    return (error as { errors?: { code?: string }[] })?.errors?.[0]?.code;
  }
}
